import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import client, {
  ONBOARDING_DISTRIBUTOR_ID,
  clearMerchantConditionsAcceptedLocal,
  clearPartnerConditionsAcceptedLocal,
  getMerchantConditionsAcceptedLocal,
  getPartnerConditionsAcceptedLocal,
  setOnboardingApplicationId,
  setOnboardingToken,
} from '../../api/client';
import { acceptMerchantConditions } from './checklistApi';
import type { AxiosProgressEvent } from 'axios';
import { RootState } from '../../store/store';
import type { BrandConfig } from '../../api/brand';
import type { ChecklistApplication } from './checklistApi';
import type { PartnerProfile } from './partnerApi';

export type OnboardingView = 'login' | 'hub' | 'wizard' | 'conditions' | 'agreement' | 'directDebit' | 'partnerDetails';

export type CompanyType = 'COMPANY' | 'INDIVIDUAL' | 'SOLE_TRADER';

interface MerchantData {
  email: string;
  phone: string;
  phoneCountryCode: string;
  ownerPhoneLocal: string;
  contactFirstName: string;
  contactLastName: string;
  companyType: CompanyType;
  companyName: string;
  companyRegistrationNumber: string;
  vatNumber: string;
  website: string;
  street: string;
  streetLine2: string;
  city: string;
  province: string;
  zip: string;
  country: string;
  currency: string;
  timezone: string;
  sellType: string;
  tradeName: string;
  businessDescription: string;
  statementDescriptor: string;
  maxTransactionUsd: string;
  annualRevenueUsd: string;
  avgTransactionUsd: string;
  avgFulfillmentDays: string;
  sameAsRegisteredAddress: boolean;
  physicalStreet: string;
  physicalStreetLine2: string;
  physicalCity: string;
  physicalProvince: string;
  physicalZip: string;
  physicalCountry: string;
  ownerDob: string;
  ownerNationality: string;
  ownerAdditionalCitizenships: string;
  ownerStreet: string;
  ownerStreetLine2: string;
  ownerCity: string;
  ownerProvince: string;
  ownerZip: string;
  ownerCountry: string;
  ownerIdType: string;
  ownerIdNumber: string;
  bankName: string;
  accountNumber: string;
  iban: string;
  swift: string;
  sortCode: string;
  hasWhatsAppSupportAdded: boolean;
  authorizedDirectDebit: boolean;
  signedContract: boolean;
  awareDemoMode: boolean;
  acceptTerms: boolean;
}

export interface ShareholderInput {
  firstName: string;
  lastName: string;
  role: string;
  sharePercent: string;
  email: string;
  phone: string;
  phoneCountryCode: string;
  dob: string;
  nationality: string;
  additionalCitizenships: string;
  street: string;
  streetLine2: string;
  city: string;
  province: string;
  zip: string;
  country: string;
  idType: string;
  idNumber: string;
  passportFile: File | null;
  addressProofFile: File | null;
  idBackFile: File | null;
}

export type OnboardingFiles = {
  companyRegistration: File | null;
  proofOfAddress: File | null;
  proofOfAccount: File | null;
  businessProofOfAddress: File | null;
  ownerPassport: File | null;
  ownerProofOfAddress: File | null;
  ownerIdBack: File | null;
  shareDistribution?: File | null;
};

interface OnboardingState {
  view: OnboardingView;
  activeStep: number;
  formData: MerchantData;
  files: OnboardingFiles;
  shareholders: ShareholderInput[];
  applicationId: number | null;
  checklist: ChecklistApplication | null;
  /** Client-only merchant conditions acceptance (before application exists). */
  conditionsAcceptedLocal: boolean;
  partnerProfile: PartnerProfile | null;
  /** Client-only partner conditions acceptance (before a distributor row exists). */
  partnerConditionsAcceptedLocal: boolean;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  uploadProgress: Record<string, number>;
  brandConfig: BrandConfig | null;
}

const emptyShareholder = (): ShareholderInput => ({
  firstName: '',
  lastName: '',
  role: '',
  sharePercent: '',
  email: '',
  phone: '',
  phoneCountryCode: '+44',
  dob: '',
  nationality: '',
  additionalCitizenships: '',
  street: '',
  streetLine2: '',
  city: '',
  province: '',
  zip: '',
  country: '',
  idType: '',
  idNumber: '',
  passportFile: null,
  addressProofFile: null,
  idBackFile: null,
});

const initialState: OnboardingState = {
  view: 'login',
  activeStep: 0,
  formData: {
    email: '',
    phone: '',
    phoneCountryCode: '+44',
    ownerPhoneLocal: '',
    contactFirstName: '',
    contactLastName: '',
    companyType: 'COMPANY',
    companyName: '',
    companyRegistrationNumber: '',
    vatNumber: '',
    website: '',
    street: '',
    streetLine2: '',
    city: '',
    province: '',
    zip: '',
    country: '',
    currency: '',
    timezone: '',
    sellType: 'Physical Business (Brick And Mortar)',
    tradeName: '',
    businessDescription: '',
    statementDescriptor: '',
    maxTransactionUsd: '',
    annualRevenueUsd: '',
    avgTransactionUsd: '',
    avgFulfillmentDays: '',
    sameAsRegisteredAddress: true,
    physicalStreet: '',
    physicalStreetLine2: '',
    physicalCity: '',
    physicalProvince: '',
    physicalZip: '',
    physicalCountry: '',
    ownerDob: '',
    ownerNationality: '',
    ownerAdditionalCitizenships: '',
    ownerStreet: '',
    ownerStreetLine2: '',
    ownerCity: '',
    ownerProvince: '',
    ownerZip: '',
    ownerCountry: '',
    ownerIdType: '',
    ownerIdNumber: '',
    bankName: '',
    accountNumber: '',
    iban: '',
    swift: '',
    sortCode: '',
    hasWhatsAppSupportAdded: false,
    authorizedDirectDebit: false,
    signedContract: false,
    awareDemoMode: false,
    acceptTerms: false,
  },
  files: {
    companyRegistration: null,
    proofOfAddress: null,
    proofOfAccount: null,
    businessProofOfAddress: null,
    ownerPassport: null,
    ownerProofOfAddress: null,
    ownerIdBack: null,
    shareDistribution: null,
  },
  shareholders: [],
  applicationId: null,
  checklist: null,
  conditionsAcceptedLocal: typeof window !== 'undefined' ? getMerchantConditionsAcceptedLocal() : false,
  partnerProfile: null,
  partnerConditionsAcceptedLocal: typeof window !== 'undefined' ? getPartnerConditionsAcceptedLocal() : false,
  status: 'idle',
  error: null,
  uploadProgress: {},
  brandConfig: null,
};

function buildCreatePayload(formData: MerchantData) {
  return {
    email: formData.email,
    phone: formData.phone,
    phoneCountryCode: formData.phoneCountryCode,
    contactFirstName: formData.contactFirstName,
    contactLastName: formData.contactLastName,
    companyType: formData.companyType,
    companyName: formData.companyName,
    companyRegistrationNumber: formData.companyRegistrationNumber,
    vatNumber: formData.vatNumber,
    website: formData.website,
    street: formData.street,
    streetLine2: formData.streetLine2,
    city: formData.city,
    province: formData.province,
    zip: formData.zip,
    country: formData.country,
    currency: formData.currency,
    timezone: formData.timezone,
    sellType: formData.sellType,
    tradeName: formData.tradeName,
    businessDescription: formData.businessDescription,
    statementDescriptor: formData.statementDescriptor,
    maxTransactionUsd: formData.maxTransactionUsd ? Number(formData.maxTransactionUsd) : null,
    annualRevenueUsd: formData.annualRevenueUsd ? Number(formData.annualRevenueUsd) : null,
    avgTransactionUsd: formData.avgTransactionUsd ? Number(formData.avgTransactionUsd) : null,
    avgFulfillmentDays: formData.avgFulfillmentDays ? Number(formData.avgFulfillmentDays) : null,
    physicalStreet: formData.physicalStreet,
    physicalStreetLine2: formData.physicalStreetLine2,
    physicalCity: formData.physicalCity,
    physicalProvince: formData.physicalProvince,
    physicalZip: formData.physicalZip,
    physicalCountry: formData.physicalCountry,
    ownerDob: formData.ownerDob || null,
    ownerNationality: formData.ownerNationality || null,
    ownerAdditionalCitizenships: formData.ownerAdditionalCitizenships || null,
    ownerStreet: formData.ownerStreet || null,
    ownerStreetLine2: formData.ownerStreetLine2 || null,
    ownerCity: formData.ownerCity || null,
    ownerProvince: formData.ownerProvince || null,
    ownerZip: formData.ownerZip || null,
    ownerCountry: formData.ownerCountry || null,
    ownerIdType: formData.ownerIdType || null,
    ownerIdNumber: formData.ownerIdNumber || null,
    bankName: formData.bankName || null,
    accountNumber: formData.accountNumber || null,
    iban: formData.iban || null,
    swift: formData.swift || null,
    sortCode: formData.sortCode || null,
    hasWhatsAppSupportAdded: formData.hasWhatsAppSupportAdded,
    authorizedDirectDebit: formData.authorizedDirectDebit,
    signedContract: formData.signedContract,
    awareDemoMode: formData.awareDemoMode,
    acceptTerms: formData.acceptTerms,
  };
}

export const submitOnboarding = createAsyncThunk(
  'onboarding/submit',
  async (_, { getState, rejectWithValue, dispatch }) => {
    const state = getState() as RootState;
    const { formData, files, shareholders } = state.onboarding;
    const brandHost = (typeof window !== 'undefined' && window.location && window.location.hostname) ? window.location.hostname : '';

    function progressHandler(key: string) {
      return (e: AxiosProgressEvent) => {
        if (!e.total) return;
        const pct = Math.round((e.loaded! / e.total) * 100);
        dispatch(setUploadProgress({ key, value: pct }));
      };
    }

    const mapDocType = (key: string): string | null => {
      switch (key) {
        case 'companyRegistration': return 'COMPANY_REGISTRATION_CERTIFICATE';
        case 'proofOfAddress': return 'PROOF_OF_COMPANY_ADDRESS';
        case 'proofOfAccount': return 'PROOF_OF_BANK_ACCOUNT';
        case 'businessProofOfAddress': return 'BUSINESS_PROOF_OF_ADDRESS';
        case 'ownerPassport': return 'OWNER_PASSPORT';
        case 'ownerProofOfAddress': return 'OWNER_PROOF_OF_ADDRESS';
        case 'ownerIdBack': return 'OWNER_ID_BACK';
        case 'shareDistribution': return 'PROOF_OF_SHARE_DISTRIBUTION';
        default: return null;
      }
    };

    try {
      const payload = buildCreatePayload(formData);
      if (formData.companyType === 'COMPANY' && shareholders.length) {
        payload.contactFirstName = shareholders[0].firstName;
        payload.contactLastName = shareholders[0].lastName;
      }
      const createRes = await client.post('/applications', {
        ...payload,
        distributor_id: ONBOARDING_DISTRIBUTOR_ID,
        brand_host: brandHost,
      });
      const applicationId = createRes.data.id;
      if (!applicationId) return rejectWithValue('Failed to get application ID');
      if (createRes.data?.token) setOnboardingToken(createRes.data.token);

      const uploadPromises: Promise<any>[] = [];
      const fileEntries = Object.entries(files) as [keyof OnboardingFiles, File | null][];
      const shouldIncludeOwner = formData.companyType !== 'COMPANY';
      const keysToUpload: string[] = ['companyRegistration', 'proofOfAddress', 'proofOfAccount', 'businessProofOfAddress', 'shareDistribution'];
      if (shouldIncludeOwner) keysToUpload.push('ownerPassport', 'ownerProofOfAddress', 'ownerIdBack');

      fileEntries
        .filter(([k, file]) => !!file && keysToUpload.includes(k as string))
        .forEach(([k, file]) => {
          const docType = mapDocType(k as string);
          if (!docType || !file) return;
          const fd = new FormData();
          fd.append('document', file);
          fd.append('document_type', docType);
          const key = `application_${k}`;
          uploadPromises.push(client.post(`/applications/${applicationId}/documents`, fd, { onUploadProgress: progressHandler(key) }));
        });

      if (formData.companyType === 'COMPANY' && shareholders.length) {
        for (let i = 0; i < shareholders.length; i++) {
          const sh = shareholders[i];
          try {
            const res = await client.post(`/applications/${applicationId}/shareholders`, {
              first_name: sh.firstName,
              last_name: sh.lastName,
              role: sh.role,
              share_percent: sh.sharePercent,
              email: sh.email,
              phone: sh.phoneCountryCode && sh.phone ? `${sh.phoneCountryCode}${sh.phone.replace(/\D/g, '')}` : sh.phone,
              phone_country_code: sh.phoneCountryCode,
              dob: sh.dob,
              nationality: sh.nationality,
              additional_citizenships: sh.additionalCitizenships,
              street: sh.street,
              street_line2: sh.streetLine2,
              city: sh.city,
              province: sh.province,
              zip: sh.zip,
              country: sh.country,
              id_type: sh.idType,
              id_number: sh.idNumber,
            });
            const shId = res.data?.id;
            if (shId) {
              const shDocUploads: Array<{ file: File | null; type: string; suffix: string }> = [
                { file: sh.passportFile, type: 'OWNER_PASSPORT', suffix: 'passport' },
                { file: sh.addressProofFile, type: 'OWNER_PROOF_OF_ADDRESS', suffix: 'address' },
                { file: sh.idBackFile, type: 'OWNER_ID_BACK', suffix: 'idback' },
              ];
              for (const item of shDocUploads) {
                if (!item.file) continue;
                const fd = new FormData();
                fd.append('document', item.file);
                fd.append('document_type', item.type);
                const key = `appsh_${shId}_${item.suffix}`;
                uploadPromises.push(client.post(`/applications/${applicationId}/shareholders/${shId}/documents`, fd, { onUploadProgress: progressHandler(key) }));
              }
            }
          } catch (_) { /* continue next shareholder */ }
        }
      }

      await Promise.all(uploadPromises);

      // Persist client-only merchant conditions acceptance now that we have an application.
      if (getMerchantConditionsAcceptedLocal()) {
        try {
          await acceptMerchantConditions(applicationId);
          clearMerchantConditionsAcceptedLocal();
        } catch (_) {
          // Hub can retry accept once applicationId exists.
        }
      }

      return applicationId;
    } catch (err: any) {
      const stringifyFieldError = (value: unknown): string => {
        if (typeof value === 'string') return value.trim();
        if (typeof value === 'number' || typeof value === 'boolean') return String(value);
        return '';
      };

      const collectFieldErrors = (value: unknown): string[] => {
        if (Array.isArray(value)) {
          return value.map((item) => collectFieldErrors(item)).flat().filter(Boolean);
        }
        if (value && typeof value === 'object') {
          return Object.values(value as Record<string, unknown>).map((item) => collectFieldErrors(item)).flat().filter(Boolean);
        }
        const text = stringifyFieldError(value);
        return text ? [text] : [];
      };

      const code = err?.response?.data?.code;
      const message = err?.response?.data?.message || 'An unknown error occurred';
      if (code === 'TOKEN_EXPIRED' || code === 'UNAUTHENTICATED') {
        dispatch(setActiveStep(0));
        dispatch(setView('login'));
        return rejectWithValue('Your session has expired. Please start again from the first step.');
      }
      if (code === 'VALIDATION_ERROR') {
        const fields = err?.response?.data?.fields;
        const uniqueFieldErrors = Array.from(new Set(collectFieldErrors(fields)));
        const parts = uniqueFieldErrors.join(', ');
        return rejectWithValue(parts ? `Invalid: ${parts}` : message);
      }
      return rejectWithValue(message);
    }
  }
);

const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    setView(state, action: PayloadAction<OnboardingView>) {
      state.view = action.payload;
    },
    setChecklist(state, action: PayloadAction<ChecklistApplication | null>) {
      state.checklist = action.payload;
      if (action.payload?.id) {
        state.applicationId = action.payload.id;
        setOnboardingApplicationId(action.payload.id);
      }
      if (action.payload?.merchant_conditions_status === 'COMPLETED') {
        state.conditionsAcceptedLocal = true;
        clearMerchantConditionsAcceptedLocal();
      }
    },
    setConditionsAcceptedLocal(state, action: PayloadAction<boolean>) {
      state.conditionsAcceptedLocal = action.payload;
    },
    setPartnerProfile(state, action: PayloadAction<PartnerProfile | null>) {
      state.partnerProfile = action.payload;
      if (action.payload?.partner_conditions_status === 'COMPLETED') {
        state.partnerConditionsAcceptedLocal = true;
        clearPartnerConditionsAcceptedLocal();
      }
    },
    setPartnerConditionsAcceptedLocal(state, action: PayloadAction<boolean>) {
      state.partnerConditionsAcceptedLocal = action.payload;
    },
    setActiveStep(state, action: PayloadAction<number>) {
      state.activeStep = action.payload;
    },
    updateFormData(state, action: PayloadAction<Partial<OnboardingState['formData']>>) {
      state.formData = { ...state.formData, ...action.payload };
    },
    updateFiles(state, action: PayloadAction<Partial<OnboardingState['files']>>) {
      state.files = { ...state.files, ...action.payload };
    },
    addShareholder(state) {
      state.shareholders.push(emptyShareholder());
    },
    updateShareholder(state, action: PayloadAction<{ index: number; data: Partial<ShareholderInput> }>) {
      const { index, data } = action.payload;
      state.shareholders[index] = { ...state.shareholders[index], ...data };
    },
    removeShareholder(state, action: PayloadAction<number>) {
      state.shareholders.splice(action.payload, 1);
    },
    setUploadProgress(state, action: PayloadAction<{ key: string; value: number }>) {
      state.uploadProgress[action.payload.key] = action.payload.value;
    },
    setBrandConfig(state, action: PayloadAction<BrandConfig>) {
      state.brandConfig = action.payload;
    },
    resetForm: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitOnboarding.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(submitOnboarding.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.error = null;
        state.applicationId = action.payload as number;
        setOnboardingApplicationId(action.payload as number);
        state.view = 'hub';
        state.checklist = {
          ...(state.checklist || {} as ChecklistApplication),
          id: action.payload as number,
          onboarding_form_status: 'COMPLETED',
          onboarding_form_completed_at: new Date().toISOString(),
          merchant_conditions_status: state.conditionsAcceptedLocal || getMerchantConditionsAcceptedLocal()
            ? 'COMPLETED'
            : (state.checklist?.merchant_conditions_status || 'NOT_STARTED'),
          merchant_conditions_accepted_at: state.conditionsAcceptedLocal || getMerchantConditionsAcceptedLocal()
            ? new Date().toISOString()
            : (state.checklist?.merchant_conditions_accepted_at || null),
        };
      })
      .addCase(submitOnboarding.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      });
  }
});

export const { setView, setChecklist, setConditionsAcceptedLocal, setPartnerProfile, setPartnerConditionsAcceptedLocal, setActiveStep, updateFormData, updateFiles, addShareholder, updateShareholder, removeShareholder, setUploadProgress, setBrandConfig, resetForm } = onboardingSlice.actions;
export default onboardingSlice.reducer;
